import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, ArrowUpRightIcon, ChevronDownIcon, CheckCircle2Icon, HeartIcon } from 'lucide-react';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
const NAVY = '#0b1f3f';
const BLUE = '#1565C0';
const RED = '#cf142b';
const EASE = [0.23, 1, 0.32, 1] as const;
interface NetworkOrganizationsProps {
  isEs: boolean;
}

/**
 * Expandable spotlight list for organizations that have supplied a logo.
 * Collapsed, each row shows only the logo, name, and location; expanding
 * reveals the mission, leadership, and where the organization works.
 */
export function NetworkOrganizations({
  isEs
}: NetworkOrganizationsProps) {
  const orgs = SISTER_ORGANIZATIONS.filter(o => o.logo || o.mission);
  const [open, setOpen] = useState<string | null>(orgs[0]?.name ?? null);
  if (orgs.length === 0) return null;
  return <ul className="flex flex-col gap-4" data-id="element-3020" data-mp="3cl8dmw556v57">
      {orgs.map(org => {
      const isOpen = open === org.name;
      const panelId = `org-panel-${org.name.replace(/\s+/g, '-')}`;
      return <li key={org.name} className="rounded-2xl border bg-white overflow-hidden" style={{
        borderColor: 'rgba(11,31,63,0.14)'
      }} data-id="element-3021" data-mp="25u989jchuvzm">
            <div className="flex items-center gap-1.5 px-5 sm:px-6 py-2 border-b" style={{
          backgroundColor: 'rgba(22,163,74,0.07)',
          borderColor: 'rgba(11,31,63,0.1)'
        }} data-id="element-3022" data-mp="2ewih84s46haw">
              <CheckCircle2Icon className="w-3.5 h-3.5 flex-shrink-0" style={{
            color: '#16a34a'
          }} aria-hidden="true" data-id="element-3023" data-mp="1m58jpw9j4o8s" />
              <span className="font-nunito text-[10px] font-bold uppercase tracking-[0.18em]" style={{
            color: '#15803d'
          }} data-id="element-3024" data-mp="1vypg9811whg5">
                {isEs ? 'Organización 501(c)(3) Registrada' : 'Registered 501(c)(3)'}
              </span>
            </div>

            <button type="button" onClick={() => setOpen(isOpen ? null : org.name)} aria-expanded={isOpen} aria-controls={panelId} className="w-full flex items-center gap-5 p-5 sm:p-6 text-left transition-colors duration-200 hover:bg-[#f5f8fc]" data-id="element-3025" data-mp="21sxv5t0oo99w">
              {org.logo ? <img src={org.logo} alt={`${org.name} logo`} className="w-32 h-20 sm:w-56 sm:h-28 flex-shrink-0 rounded-xl object-contain bg-white" data-id="element-3026" data-mp="1ntokt9z5s9du" /> : <div aria-hidden="true" className="w-32 h-20 sm:w-56 sm:h-28 flex-shrink-0 rounded-xl flex items-center justify-center font-bebas text-3xl sm:text-4xl tracking-[0.04em] text-white" style={{
            backgroundColor: org.accent
          }} data-id="element-3027" data-mp="1rv04yzz9k2en">
                  {monogram(org.name)}
                </div>}
              <div className="min-w-0 flex-1" data-id="element-3028" data-mp="3bta9qkospw01">
                <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-1.5" style={{
              color: BLUE
            }} data-id="element-3029" data-mp="3ftkddqp48abq">
                  {isEs ? org.focusEs : org.focus}
                </p>
                <h3 className="font-bebas text-2xl sm:text-3xl tracking-[0.03em] leading-tight mb-1.5" style={{
              color: NAVY
            }} data-id="element-3030" data-mp="36tnw4a522dqp">
                  {org.name}
                </h3>
                <p className="inline-flex items-center gap-1.5 font-nunito text-xs uppercase tracking-[0.12em]" style={{
              color: 'rgba(11,31,63,0.55)'
            }} data-id="element-3031" data-mp="3ks4404enmaaf">
                  <MapPinIcon className="w-3 h-3 flex-shrink-0" style={{
                color: RED
              }} data-id="element-3032" data-mp="haltfp7hyp6b" />
                  {org.location}
                  {org.serviceArea ? ` · ${isEs ? org.serviceAreaEs : org.serviceArea}` : ''}
                </p>
              </div>
              <ChevronDownIcon className="w-5 h-5 flex-shrink-0 transition-transform duration-200" style={{
            color: NAVY,
            transform: isOpen ? 'rotate(180deg)' : 'none'
          }} aria-hidden="true" data-id="element-3033" data-mp="2plo8zfov2it5" />
            </button>

            <AnimatePresence initial={false} data-id="element-3034" data-mp="38abh672c3oay">
              {isOpen ? <motion.div id={panelId} initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.26,
            ease: EASE
          }} className="overflow-hidden" data-id="element-3035" data-mp="38cfh6dx4435y">
                  <div className="border-t" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3036" data-mp="3r7w3p7hmmase">
                    <div className="grid md:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]" data-id="element-3037" data-mp="2h27guxjgjsk">
                      {/* Mission + where they work */}
                      <div className="px-5 sm:px-7 py-7" data-id="element-3038" data-mp="cbu2ps3neuk6">
                        <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{
                    color: 'rgba(11,31,63,0.4)'
                  }} data-id="element-3039" data-mp="hbyo9wjeyfh9">
                          {isEs ? 'Misión' : 'Mission'}
                        </p>
                        <p className="font-nunito text-[17px] leading-[1.65] mb-7" style={{
                    color: 'rgba(11,31,63,0.82)'
                  }} data-id="element-3040" data-mp="v3my1t66wrv2">
                          {isEs ? org.missionEs || org.descriptionEs : org.mission || org.description}
                        </p>

                        {org.partnerSites && org.partnerSites.length > 0 ? <div className="pt-6 border-t" style={{
                    borderColor: 'rgba(11,31,63,0.1)'
                  }} data-id="element-3041" data-mp="361o3y3rpcrqm">
                            <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{
                      color: 'rgba(11,31,63,0.4)'
                    }} data-id="element-3042" data-mp="3exdj3v2k4pvk">
                              {isEs ? 'Su trabajo en Honduras' : 'Their work in Honduras'}
                            </p>
                            <ul className="flex flex-col gap-2" data-id="element-3043" data-mp="3ttd1pznam8ey">
                              {org.partnerSites.map(site => <li key={site.name} className="inline-flex items-center gap-2 font-nunito text-sm font-bold" style={{
                        color: NAVY
                      }} data-id="element-3044" data-mp="23g5gnfbh1ju1">
                                  <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" style={{
                          color: RED
                        }} data-id="element-3045" data-mp="1tdi9e6s60x0v" />
                                  {site.location}
                                </li>)}
                            </ul>
                          </div> : null}
                      </div>

                      {/* Leadership panel */}
                      {org.director ? <div className="px-5 sm:px-7 py-7 border-t md:border-t-0 md:border-l flex flex-col" style={{
                  borderColor: 'rgba(11,31,63,0.1)',
                  backgroundColor: '#f6f9fc'
                }} data-id="element-3046" data-mp="woan2wjq2jvw">
                          <div className="flex items-center gap-5" data-id="element-3047" data-mp="8elpan11bgyf">
                            {/* Constant portrait frame — every organization's
                                leadership photo renders at the same size and
                                crop regardless of the source image. */}
                            <div className="w-44 h-56 sm:w-52 sm:h-64 flex-shrink-0 rounded-lg overflow-hidden bg-white p-2" style={{
                      border: '1px solid rgba(11,31,63,0.16)',
                      boxShadow: '0 1px 3px rgba(11,31,63,0.08)'
                    }} data-id="element-3048" data-mp="3qt4crzlq4sgy">
                              <img src={org.director.photo} alt={org.director.name || `${org.name} ${org.director.title}`} className="w-full h-full rounded object-cover object-top" data-id="element-3049" data-mp="37ua0cx9qg43y" />
                            </div>
                            <div className="min-w-0" data-id="element-3050" data-mp="2sxsciu64bifx">
                              {org.director.name ? <p className="font-bebas text-2xl tracking-[0.03em] leading-tight" style={{
                        color: NAVY
                      }} data-id="element-3051" data-mp="2xqv5b032mnv2">
                                  {org.director.name}
                                </p> : null}
                              <p className="font-nunito text-[11px] font-bold uppercase tracking-[0.16em] mt-1" style={{
                        color: BLUE
                      }} data-id="element-3052" data-mp="2e1kp7kzey0wf">
                                {isEs ? org.director.titleEs : org.director.title}
                              </p>
                              {org.director.linkedin ? <a href={org.director.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                        color: 'rgba(11,31,63,0.55)'
                      }} data-id="element-3053" data-mp="25njq856eo1jl">
                                  LinkedIn
                                  <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3054" data-mp="1rmz87b8be9r5" />
                                </a> : null}
                              {org.director.profileUrl ? <a href={org.director.profileUrl} className="flex items-center gap-1.5 mt-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                        color: 'rgba(11,31,63,0.55)'
                      }} data-id="element-3055" data-mp="332e226588li1">
                                  {isEs ? 'Ver Perfil' : 'View Profile'}
                                  <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3056" data-mp="hqaktc26cxck" />
                                </a> : null}
                            </div>
                          </div>
                        </div> : null}
                    </div>

                    {/* Actions — matched buttons across the full width */}
                    {org.donateUrl || org.url ? <div className="px-5 sm:px-7 py-5 border-t flex flex-col sm:flex-row gap-3" style={{
                borderColor: 'rgba(11,31,63,0.1)'
              }} data-id="element-3057" data-mp="3uxm0c9eyxzby">
                        {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: RED
                }} data-id="element-3058" data-mp="2c9sy9ssrq83h">
                            {isEs ? 'Visitar sitio web' : 'Visit Website'}
                            <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3059" data-mp="2zx0znzn2sx08" />
                          </a> : null}
                        {org.donateUrl ? <a href={org.donateUrl} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: '#4FA8E8'
                }} data-id="element-3060" data-mp="2qwvuzx99ocpr">
                            <HeartIcon className="w-3.5 h-3.5" data-id="element-3061" data-mp="1gznx32ft3e76" />
                            {isEs ? 'Donar / Participar' : 'Donate / Get Involved'}
                          </a> : null}
                      </div> : null}
                  </div>
                </motion.div> : null}
            </AnimatePresence>
          </li>;
    })}
    </ul>;
}

/** Two-letter stand-in shown until an organization supplies its logo. */
function monogram(name: string): string {
  // Acronym-led names ("ASJ — …", "CRC …") use the acronym itself.
  const first = name.split(/\s/)[0];
  if (/^[A-Z]{2,4}$/.test(first)) return first;
  const words = name.split(/[\s—-]+/).filter(w => /^[A-Za-zÀ-ÿ]/.test(w) && !['de', 'for', 'a', 'the'].includes(w.toLowerCase()));
  return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
}